import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice, createSelector } from '@reduxjs/toolkit';

import type { RootState } from 'features/app/store/store';
import type { VideoPrivacyState } from 'features/video-privacy/interfaces';

export const initialVideoPrivacyState: VideoPrivacyState = {
  local: {
    isEnabled: false,
    isLoading: false,
  },
  remote: {
    isEnabled: false,
    isOnHold: false,
  },
};

const videoPrivacySlice = createSlice({
  name: 'video-privacy',
  initialState: initialVideoPrivacyState,
  reducers: {
    enableLocalVideoPrivacy: (state) => {
      state.local.isEnabled = true;
    },
    disableLocalVideoPrivacy: (state) => {
      state.local.isEnabled = false;
    },
    startLocalVideoPrivacyLoading: (state) => {
      state.local.isLoading = true;
    },
    stopLocalVideoPrivacyLoading: (state) => {
      state.local.isLoading = false;
    },
    remoteVideoPrivacyChanged: (
      state,
      action: PayloadAction<{ isEnabled: boolean; isOnHold: boolean }>
    ) => {
      state.remote.isEnabled = action.payload.isEnabled;
      state.remote.isOnHold = action.payload.isOnHold;
    },
    resetVideoPrivacy: () => initialVideoPrivacyState,
  },
});

export const {
  enableLocalVideoPrivacy,
  disableLocalVideoPrivacy,
  startLocalVideoPrivacyLoading,
  stopLocalVideoPrivacyLoading,
  remoteVideoPrivacyChanged,
  resetVideoPrivacy,
} = videoPrivacySlice.actions;

export const videoPrivacyReducer = videoPrivacySlice.reducer;

export const isLocalVideoPrivacyEnabledSelector = ({
  videoPrivacy,
}: RootState) => videoPrivacy.local.isEnabled;

export const isLocalVideoPrivacyLoadingSelector = ({
  videoPrivacy,
}: RootState) => videoPrivacy.local.isLoading;

export const isRemoteVideoPrivacyEnabledSelector = ({
  videoPrivacy,
}: RootState) => videoPrivacy.remote.isEnabled;

export const isRemoteVideoOnHoldSelector = ({ videoPrivacy }: RootState) =>
  videoPrivacy.remote.isOnHold;

export const selectRemoteVideoState = createSelector(
  isRemoteVideoPrivacyEnabledSelector,
  isRemoteVideoOnHoldSelector,
  (isPrivacyEnabled, isOnHold) => ({
    isPrivacyEnabled,
    isOnHold,
  })
);
